import { useEffect, useState } from 'react';
import { useScrollLock } from '../../components/useScrollLock.ts';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Button, Icons, Spinner, cx } from '../../components/ui.tsx';
import { FileTypeIcon } from '../../components/icons.tsx';
import { api, authedFetch } from '../../lib/api.ts';
import { formatBytes } from '../../lib/display.ts';

/**
 * Looking at a file without leaving the system.
 *
 * The client's workflow was a folder of email attachments, so "can I open
 * it?" has to be yes for whatever arrives — a PDF invoice, a Word contract, a
 * scanned receipt, a phone video of a delivery. The server decides how each
 * one can be shown; this only renders what it is told.
 *
 * Every fetch goes through the API client rather than a bare `src` URL,
 * because the access token lives in memory and a plain <img src> cannot carry
 * an Authorization header. The bytes become a blob URL, which is revoked when
 * the viewer closes so nothing is left holding megabytes of a video.
 */

interface PreviewPlan {
  mode: 'native' | 'text' | 'converted' | 'sheet' | 'none';
  servedType: string;
  name: string;
  sizeBytes: number;
  sourceType: string;
  reason?: string;
}

export function FileViewer({
  fileId,
  name,
  mimeType,
  onClose,
  onDownload,
  onExtract,
}: {
  fileId: string;
  name: string;
  mimeType: string;
  onClose: () => void;
  /**
   * Absent for an account that may not download.
   *
   * The viewer is the one place a file can be read without a copy of it
   * leaving, so it is the one place the button has to be able to go.
   */
  onDownload?: (() => void) | undefined;
  /**
   * Unpack it, where it is an archive and the account may write.
   *
   * Absent means neither, and the screen falls back to offering the download
   * on its own — which is what it did for every kind of file it cannot show,
   * including the one kind it could do something useful with.
   */
  onExtract?: (() => void) | undefined;
}) {
  const plan = useQuery({
    queryKey: ['preview-plan', fileId],
    queryFn: () => api.get<PreviewPlan>(`/api/drive/files/${fileId}/preview-plan`),
  });

  // The Drive behind this stays where it is — see useScrollLock for why
  // that needs saying at all.
  useScrollLock();

  // Escape closes, as it does in every other viewer anyone has used.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex flex-col bg-[rgba(9,17,28,0.82)]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className="flex shrink-0 items-center gap-3 px-4 py-3">
        <FileTypeIcon name={name} mimeType={mimeType} size={22} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-medium text-white">{name}</p>
          {plan.data ? (
            <p className="text-micro text-[var(--blue-200)]">
              {formatBytes(plan.data.sizeBytes)}
              {plan.data.mode === 'converted' ? ' · shown as PDF' : ''}
            </p>
          ) : null}
        </div>

        {onDownload ? (
          <Button
            icon={<Icons.Download />}
            onClick={onDownload}
            className="border-white/25 bg-white/10 text-white hover:bg-white/20"
          >
            Download
          </Button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the viewer"
          className="rounded-md p-2 text-white/80 transition-fast hover:bg-white/15 hover:text-white"
        >
          <Icons.Close size={20} />
        </button>
      </header>

      {/* Padding is a desktop luxury: on a phone those sixteen pixels either
          side are a tenth of the screen, taken from the thing being read. */}
      <div className="min-h-0 flex-1 pb-2 max-md:px-0 md:px-4 md:pb-4">
        <div className="flex h-full items-center justify-center overflow-hidden rounded-xl bg-surface">
          {plan.isLoading ? (
            <Spinner label="Opening" />
          ) : plan.isError || !plan.data ? (
            <Unavailable
              name={name}
              mimeType={mimeType}
              reason="This file could not be opened."
              onDownload={onDownload}
            />
          ) : plan.data.mode === 'sheet' ? (
            // A workbook that has somehow reached the viewer. Showing a
            // picture of it would be the wrong answer to "open this".
            <Unavailable
              name={name}
              mimeType={mimeType}
              reason="This is a spreadsheet. Open it for editing rather than viewing it as a picture — use Open for editing in its menu."
              onDownload={onDownload}
            />
          ) : plan.data.mode === 'none' ? (
            // Where a zip ends up, and the one case with something better to
            // offer than the download.
            <Unavailable
              name={name}
              mimeType={mimeType}
              reason={
                // The server's line ends "Download it to open it", which is
                // no longer the only thing to do with an archive.
                onExtract
                  ? 'There is no viewer for an archive, but its contents can be unpacked into the Drive.'
                  : (plan.data.reason ?? 'There is no viewer for this kind of file.')
              }
              onDownload={onDownload}
              onExtract={onExtract}
            />
          ) : (
            <Content
              fileId={fileId}
              plan={plan.data}
              name={name}
              mimeType={mimeType}
              onDownload={onDownload}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The bytes themselves.
 *
 * Held as a blob URL rather than pointed at the API route directly: the route
 * needs an Authorization header, and none of `<img>`, `<video>` or `<iframe>`
 * can send one.
 */
function Content({
  fileId,
  plan,
  name,
  mimeType,
  onDownload,
}: {
  fileId: string;
  plan: PreviewPlan;
  name: string;
  mimeType: string;
  onDownload?: (() => void) | undefined;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const response = await authedFetch(`/api/drive/files/${fileId}/preview`, {
          credentials: 'include',
        });

        if (!response.ok) {
          const detail = (await response.json().catch(() => null)) as { message?: string } | null;
          throw new Error(detail?.message ?? 'That file could not be opened.');
        }

        if (plan.mode === 'text') {
          const body = await response.text();
          if (!cancelled) setText(body);
          return;
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      } catch (caught) {
        if (!cancelled) {
          setFailed(caught instanceof Error ? caught.message : 'That file could not be opened.');
        }
      }
    })();

    return () => {
      cancelled = true;
      // Without this the browser holds the whole file in memory for the life
      // of the tab — which for a video is the difference between a preview
      // and a memory leak.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, plan.mode]);

  if (failed) {
    return <Unavailable name={name} mimeType={mimeType} reason={failed} onDownload={onDownload} />;
  }

  if (plan.mode === 'text') {
    if (text === null) return <Converting mode={plan.mode} />;
    return (
      <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-words p-6 font-mono text-body-sm text-ink">
        {text}
      </pre>
    );
  }

  if (!url) return <Converting mode={plan.mode} />;

  const served = plan.servedType;

  if (served.startsWith('image/')) {
    return <img src={url} alt={name} className="max-h-full max-w-full object-contain" />;
  }

  if (served.startsWith('video/')) {
    return <video src={url} controls className="max-h-full max-w-full" />;
  }

  if (served.startsWith('audio/')) {
    return (
      <div className="flex flex-col items-center gap-4 p-8">
        <FileTypeIcon name={name} mimeType={mimeType} size={64} />
        <audio src={url} controls className="w-96 max-w-full" />
      </div>
    );
  }

  /*
   * PDF, and everything converted into one.
   *
   * The fragment is not decoration: it is the only say we get over the
   * browser's own PDF viewer. `navpanes=0` closes the thumbnail rail, which
   * on a phone-width frame took half the room and left the page squeezed into
   * the other half; `view=Fit` fits the whole page into the frame, so it
   * arrives whole rather than scrolled sideways with its left edge cut off —
   * which is exactly how the client's letterhead came up. `toolbar` is left
   * on: printing and rotating from inside the viewer are worth the strip.
   */
  return (
    <iframe src={`${url}#view=Fit&navpanes=0`} title={name} className="h-full w-full border-0" />
  );
}

function Converting({ mode }: { mode: PreviewPlan['mode'] }) {
  return (
    <div className="flex flex-col items-center gap-3 p-10 text-center">
      <Spinner />
      <p className="text-body-sm text-muted">
        {mode === 'converted'
          ? 'Converting this document so it can be shown. The first time takes a few seconds; after that it is instant.'
          : 'Loading…'}
      </p>
    </div>
  );
}

function Unavailable({
  name,
  mimeType,
  reason,
  onDownload,
  onExtract,
}: {
  name: string;
  mimeType: string;
  reason: string;
  onDownload?: (() => void) | undefined;
  onExtract?: (() => void) | undefined;
}) {
  return (
    <div className={cx('flex max-w-md flex-col items-center gap-4 p-10 text-center')}>
      <FileTypeIcon name={name} mimeType={mimeType} size={56} />
      <div>
        <p className="text-body font-medium text-ink">{name}</p>
        <p className="mt-1.5 text-body-sm text-muted">{reason}</p>
      </div>
      {/*
        A zip has something better to offer than a download.

        Extracting is the primary action where it is available: the file is
        already in the Drive, and what the user wants is what is inside it —
        not a copy on their computer to unpack and upload back one at a time.
      */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onExtract ? (
          <Button variant="primary" icon={<Icons.Archive />} onClick={onExtract}>
            Extract here
          </Button>
        ) : null}
        {onDownload ? (
          onExtract ? (
            <Button icon={<Icons.Download />} onClick={onDownload}>
              Download
            </Button>
          ) : (
            <Button variant="primary" icon={<Icons.Download />} onClick={onDownload}>
              Download instead
            </Button>
          )
        ) : null}
      </div>
      {onExtract ? (
        <p className="text-micro text-muted">
          The contents are unpacked into a folder of their own, beside the archive.
        </p>
      ) : null}
    </div>
  );
}
